import { NavLink } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../../features/auth/authSlice';
import { 
  FaHome, 
  FaBook, 
  FaUserGraduate, 
  FaUser, 
  FaCog, 
  FaSignOutAlt,
  FaTimes,
  FaVideo,
  FaFileAlt,
  FaQuestionCircle,
  FaChartBar,
  FaChartLine,
  FaUsers,
  FaUserTie,
  FaCrown,
  FaPlus,
  FaDownload,
  FaCalendarAlt,
  FaTrophy,
  FaBell,
  FaClock,
  FaBroadcastTower,
  FaGamepad
} from 'react-icons/fa';

const Sidebar = ({ isOpen, toggleSidebar, darkMode }) => {
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  
  const handleLogout = () => {
    dispatch(logout());
  };

  // Active link style
  const activeLink = 'bg-primary text-white';
  const normalLink = darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100';

  const getRoleBasedNavigation = () => {
    switch (user?.role) {
      case 'student':
        return [
          { to: '/dashboard', icon: <FaHome />, label: 'Dashboard' },
          { to: '/dashboard/courses', icon: <FaBook />, label: 'My Courses' },
          { to: '/dashboard/live-class', icon: <FaBroadcastTower />, label: 'Live Classes' },
          { to: '/dashboard/lessons', icon: <FaVideo />, label: 'Video Lectures' },
          { to: '/dashboard/gamification', icon: <FaGamepad />, label: 'Gamification' },
          { to: '/dashboard/assignments', icon: <FaFileAlt />, label: 'Assignments' },
          { to: '/dashboard/notes', icon: <FaDownload />, label: 'Download Notes' },
          { to: '/dashboard/progress', icon: <FaTrophy />, label: 'My Progress' },
          { to: '/dashboard/calendar', icon: <FaCalendarAlt />, label: 'Calendar' },
          { to: '/dashboard/notifications', icon: <FaBell />, label: 'Notifications' },
          { to: '/dashboard/profile', icon: <FaUser />, label: 'Profile' },
          { to: '/dashboard/settings', icon: <FaCog />, label: 'Settings' }
        ];
      case 'instructor':
        return [
          { to: '/dashboard', icon: <FaHome />, label: 'Dashboard' },
          { to: '/dashboard/courses', icon: <FaBook />, label: 'My Courses' },
          { to: '/dashboard/courses/create', icon: <FaPlus />, label: 'Create Course' },
          { to: '/dashboard/live-class', icon: <FaBroadcastTower />, label: 'Live Classes' },
          { to: '/dashboard/lessons', icon: <FaVideo />, label: 'Manage Lessons' },
          { to: '/dashboard/gamification', icon: <FaGamepad />, label: 'Gamification' },
          { to: '/dashboard/assignments', icon: <FaFileAlt />, label: 'Grade Assignments' },
          { to: '/dashboard/students', icon: <FaUserGraduate />, label: 'My Students' },
          { to: '/dashboard/analytics', icon: <FaChartBar />, label: 'Analytics' },
          { to: '/dashboard/profile', icon: <FaUser />, label: 'Profile' },
          { to: '/dashboard/settings', icon: <FaCog />, label: 'Settings' }
        ];
      case 'admin':
        return [
          { to: '/dashboard', icon: <FaHome />, label: 'Dashboard' },
          { to: '/dashboard/courses', icon: <FaBook />, label: 'All Courses' },
          { to: '/dashboard/courses/pending', icon: <FaClock />, label: 'Pending Approvals' },
          { to: '/dashboard/live-class', icon: <FaBroadcastTower />, label: 'Live Classes' },
          { to: '/dashboard/users', icon: <FaUsers />, label: 'Manage Users' },
          { to: '/dashboard/instructors', icon: <FaUserTie />, label: 'Instructors' },
          
          // Student Management Section
          { to: '/dashboard/students', icon: <FaUserGraduate />, label: 'Student Management' },
          { to: '/dashboard/enrollments', icon: <FaBook />, label: 'Enrollments' },
          { to: '/dashboard/progress', icon: <FaChartLine />, label: 'Student Progress' },
          { to: '/dashboard/support/create', icon: <FaQuestionCircle />, label: 'Student Support' },
          
          { to: '/dashboard/analytics', icon: <FaChartBar />, label: 'Site Analytics' },
          { to: '/dashboard/reports', icon: <FaFileAlt />, label: 'Reports' },
          { to: '/dashboard/settings', icon: <FaCog />, label: 'System Settings' }
        ];
      default:
        return [
          { to: '/dashboard', icon: <FaHome />, label: 'Dashboard' },
          { to: '/dashboard/courses', icon: <FaBook />, label: 'My Courses' },
          { to: '/dashboard/live-class', icon: <FaBroadcastTower />, label: 'Live Classes' },
          { to: '/dashboard/profile', icon: <FaUser />, label: 'Profile' },
          { to: '/dashboard/settings', icon: <FaCog />, label: 'Settings' }
        ];
    }
  };

  const getRoleIcon = () => {
    switch (user?.role) {
      case 'admin':
        return <FaCrown className="text-yellow-500" />;
      case 'instructor':
        return <FaUserTie className="text-blue-500" />;
      case 'student':
        return <FaUserGraduate className="text-green-500" />;
      default:
        return <FaUser className="text-gray-500" />;
    }
  };

  const getRoleLabel = () => {
    switch (user?.role) {
      case 'admin':
        return 'Administrator';
      case 'instructor':
        return 'Instructor';
      case 'student':
        return 'Student';
      default:
        return 'User';
    }
  };

  return (
    <>
      {/* Mobile sidebar backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-20 bg-black bg-opacity-50 lg:hidden"
          onClick={toggleSidebar}
        ></div>
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-30 w-64 ${darkMode ? 'bg-gray-800 text-white' : 'bg-white'} shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="flex flex-col h-full">
          {/* Sidebar header */}
          <div className={`flex items-center justify-between px-4 py-5 ${darkMode ? 'border-gray-700' : 'border-b'}`}>
            <div className="flex items-center">
              <img 
                src="/logo.PNG" 
                alt="IntelUniVen Logo" 
                className="h-8 w-8 mr-3 rounded-full"
              />
              <span className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-primary'} font-solway`}>IntelUNiven LMS</span>
            </div>
            <button 
              onClick={toggleSidebar}
              className={`p-2 rounded-md lg:hidden ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              <FaTimes />
            </button>
          </div>

          {/* User info */}
          <div className={`flex flex-col items-center p-4 ${darkMode ? 'border-gray-700' : 'border-b'}`}>
            <div className={`w-20 h-20 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} flex items-center justify-center text-4xl ${darkMode ? 'text-gray-300' : 'text-gray-500'} mb-2 relative`}>
              {user?.profileImage ? (
                <img 
                  src={user.profileImage} 
                  alt={user.name} 
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <span>{user?.name?.charAt(0).toUpperCase()}</span>
              )}
              <div className={`absolute -bottom-1 -right-1 p-1 ${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-full`}>
                {getRoleIcon()}
              </div>
            </div>
            <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : ''}`}>{user?.name}</h3>
            <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>{user?.email}</p>
            <span className={`text-xs ${darkMode ? 'text-gray-400 bg-gray-700' : 'text-gray-400 bg-gray-100'} px-2 py-1 rounded-full mt-1`}>
              {getRoleLabel()}
            </span>
          </div>

          {/* Navigation */}
          <nav className={`flex-1 px-2 py-4 overflow-y-auto ${darkMode ? 'text-gray-300' : ''}`}>
            <ul className="space-y-1">
              {getRoleBasedNavigation().map((item, index) => (
                <li key={index}>
                  <NavLink 
                    to={item.to} 
                    end={item.to === '/dashboard'}
                    className={({ isActive }) => 
                      `flex items-center px-4 py-3 container-rounded-lg transition-colors ${isActive ? activeLink : normalLink}`
                    }
                  >
                    <span className="mr-3">{item.icon}</span>
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          {/* Logout button */}
          <div className={`p-4 ${darkMode ? 'border-gray-700' : 'border-t'}`}>
            <button 
              className={`flex items-center w-full px-4 py-3 ${darkMode ? 'text-red-400 hover:bg-gray-700' : 'text-red-600 hover:bg-red-50'} container-rounded-lg transition-colors`}
              onClick={handleLogout}
            >
              <FaSignOutAlt className="mr-3" />
              Logout
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;